import React, { useEffect, useState, useRef } from "react";
import {
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Pagination,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { createSearchParams, useSearchParams } from "react-router-dom";
import MiniDrawer from "../../Components/Drawer";
import { getApiData } from "../../Apis/apiHelper";
import BaseSetting from "../../Apis/setting";
import { toast } from "react-toastify";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import BaseColor from "../../Config/Color";
import { FontFamily } from "../../Config/theme";
import _ from "lodash";
import CModal from "../../Components/CModal";
import { Edit } from "@mui/icons-material";
import CInput from "../../Components/CInput";
import CButton from "../../Components/CButton";
import CSelect from "../../Components/CSelect";
import TagsInput from "../../Components/TagsInput";

const CountryMetaDetails = () => {
  const searchRef = useRef();
  const skipApplyMetaRef = useRef(false);

  const [pageLoad, setPageLoad] = useState(false);
  const [dataLoader, setDataLoader] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [pagination, setPagination] = useState({ defaultPageSize: 25, totalPage: 1 });
  const [pageSize, setPageSize] = useState(25);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [searchVal, setSearchVal] = useState("");

  const [countryMetaList, setCountryMetaList] = useState([]);
  const [rawCountryMetaRecords, setRawCountryMetaRecords] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [isEditModal, setIsEditModal] = useState(false);
  const [editId, setEditId] = useState("");
  const [disableBtn, setDisableBtn] = useState(false);
  const [createBtnLoading, setCreateBtnLoading] = useState(false);

  // Category dropdowns (same as Category create - parent, sub, two, three)
  const [parentCategoryData, setParentCategoryData] = useState([]);
  const [allCategoryData, setAllCategoryData] = useState([]);
  const [parentId, setParentId] = useState(null);
  const [subCategoryData, setSubCategoryData] = useState(null);
  const [subCategoryTwo, setSubCategoryTwo] = useState(null);
  const [subCategoryThree, setSubCategoryThree] = useState(null);

  // Country, State, City
  const [countryList, setCountryList] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);

  // Meta fields
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [metaH1, setMetaH1] = useState("");
  const [metaUrl, setMetaUrl] = useState("");
  const [tag, setTag] = useState([]);
  const [metaKeywordsInput, setMetaKeywordsInput] = useState("");

  const [metaTitleError, setMetaTitleError] = useState("");
  const [metaDescriptionError, setMetaDescriptionError] = useState("");
  const [categoryError, setCategoryError] = useState("");
  const [locationError, setLocationError] = useState("");

  const [searchParams, setSearchParams] = useSearchParams();
  const urlPage = searchParams.get("page");
  const page = urlPage && Number(urlPage) > 0 ? Number(urlPage) : 1;

  const setHeaderParams = (pageNum) => {
    setPageNo(pageNum);
    setSearchParams(createSearchParams({ page: pageNum }));
  };

  // When category or location changes in modal: fill from category meta or load existing country-meta record
  const applyMetaForCurrentSelection = React.useCallback(() => {
    if (!openModal) return;
    if (skipApplyMetaRef.current) {
      skipApplyMetaRef.current = false;
      return;
    }
    const categoryId = getSelectedCategoryId();
    if (!categoryId) {
      setMetaTitle("");
      setMetaDescription("");
      setMetaH1("");
      setMetaUrl("");
      setTag([]);
      setEditId("");
      setIsEditModal(false);
      return;
    }
    const countryName = selectedCountry?.name ?? null;
    const stateName = selectedState?.name ?? null;
    const cityName = selectedCity?.name ?? null;
    const existing = countryMetaList.find(
      (r) =>
        String(r?.category_id) === String(categoryId) &&
        (r?.country_name || "") === (countryName || "") &&
        (r?.state_name || "") === (stateName || "") &&
        (r?.city_name || "") === (cityName || "")
    );
    if (existing) {
      setEditId(existing?.id ?? "");
      setIsEditModal(true);
      setMetaTitle(existing?.meta_title || "");
      setMetaDescription(existing?.meta_description || "");
      setMetaH1(existing?.meta_h1 || "");
      setMetaUrl(existing?.meta_url || "");
      setTag(
        _.isArray(existing?.meta_keywords)
          ? existing.meta_keywords
          : _.isString(existing?.meta_keyword) && existing?.meta_keyword
            ? existing.meta_keyword.split(",").map((s) => s.trim()).filter(Boolean)
            : []
      );
    } else {
      setEditId("");
      setIsEditModal(false);
    }
  }, [
    openModal,
    parentId?.id,
    subCategoryData?.id,
    subCategoryTwo?.id,
    subCategoryThree?.id,
    selectedCountry?.name,
    selectedState?.name,
    selectedCity?.name,
    countryMetaList,
  ]);

  useEffect(() => {
    setHeaderParams(page);
  }, []);

  useEffect(() => {
    setPageLoad(true);
    getCountryMetaData(page, 25);
    getParentCategory();
    getAllCategories();
    getCountriesForDropdown();
  }, []);

  // Remap records when countryList is loaded (for display purposes - convert country ID to name)
  useEffect(() => {
    if (rawCountryMetaRecords.length > 0 && countryList.length > 0) {
      const mappedRecords = rawCountryMetaRecords.map((record) => {
        // Handle country - might be ID or name, convert to name if ID
        let countryName = record.country || "—";
        if (record.country) {
          const countryId = String(record.country);
          const countryObj = countryList.find((c) => String(c.id) === countryId);
          if (countryObj) {
            countryName = countryObj.name;
          }
        }

        const stateName = record.state || "—";
        const cityName = record.city || "—";

        return {
          ...record,
          id: record.id,
          category_id: record.category_id,
          category_name: record.category?.name || "—",
          country_name: countryName,
          state_name: stateName,
          city_name: cityName,
          meta_title: record.title || "",
          meta_description: record.meta_description || "",
          meta_h1: record.heading || "",
          meta_keyword: record.meta_key || "",
          meta_keywords: record.meta_key ? record.meta_key.split(",").map((s) => s.trim()).filter(Boolean) : [],
          meta_url: "",
          status: record.status,
        };
      });
      setCountryMetaList(mappedRecords);
    }
  }, [rawCountryMetaRecords, countryList]);

  // Fill meta from category when selection changes (category or location)
  useEffect(() => {
    applyMetaForCurrentSelection();
  }, [
    openModal,
    parentId?.id,
    subCategoryData?.id,
    subCategoryTwo?.id,
    subCategoryThree?.id,
    selectedCountry?.id,
    selectedState?.name,
    selectedCity?.id,
  ]);

  const getFilteredCategories = (parentIdVal) => {
    return Array.isArray(allCategoryData)
      ? allCategoryData.filter((cat) => cat.parent_id === parentIdVal)
      : [];
  };

  const getSelectedCategoryId = () =>
    subCategoryThree?.id ||
    subCategoryTwo?.id ||
    subCategoryData?.id ||
    parentId?.id;

  function getCountryMetaData(page = 1, limit = 25) {
    getApiData(BaseSetting.endpoint.getCountryMetaList, "post", {
      page: Number(page) || 1,
      limit: Number(limit) || 25,
    })
      .then((result) => {
        if (result?.status) {
          const response = result?.data;
          if (response?.records && Array.isArray(response.records)) {
            // Support API returning total count for server-side pagination
            const total = response?.total ?? response?.totalCount ?? response?.records?.length;
            const totalNum = typeof total === "number" ? total : response.records.length;
            setTotalRowCount(totalNum);
            const limitNum = Number(limit) || 25;
            const totalPage = response?.pagination?.totalPage ?? Math.max(1, Math.ceil(totalNum / limitNum));
            setPagination((prev) => ({
              ...prev,
              defaultPageSize: limitNum,
              totalPage,
            }));

            // Store raw records for remapping when countryList loads
            setRawCountryMetaRecords(response.records);

            // Map API response fields to match table column expectations
            const mappedRecords = response.records.map((record) => {
              // Handle country - might be ID or name, convert to name if ID
              let countryName = record.country || "—";
              if (countryList.length > 0 && record.country) {
                const countryId = String(record.country);
                const countryObj = countryList.find((c) => String(c.id) === countryId);
                if (countryObj) {
                  countryName = countryObj.name;
                }
              }

              const stateName = record.state || "—";
              const cityName = record.city || "—";

              return {
                ...record,
                id: record.id,
                category_id: record.category_id,
                category_name: record.category?.name || "—",
                country_name: countryName,
                state_name: stateName,
                city_name: cityName,
                meta_title: record.title || "",
                meta_description: record.meta_description || "",
                meta_h1: record.heading || "",
                meta_keyword: record.meta_key || "",
                meta_keywords: record.meta_key ? record.meta_key.split(",").map((s) => s.trim()).filter(Boolean) : [],
                meta_url: "",
                status: record.status,
              };
            });
            setCountryMetaList(mappedRecords);
          } else {
            setRawCountryMetaRecords([]);
            setCountryMetaList([]);
            setTotalRowCount(0);
            setPagination((prev) => ({ ...prev, totalPage: 1 }));
          }
          setTimeout(() => setPageLoad(false), 100);
        } else {
          toast(result?.message || "Failed to load data", { type: "error" });
          setPageLoad(false);
        }
      })
      .catch((err) => {
        toast(err?.message || "Error loading country meta", { type: "error" });
        setPageLoad(false);
      });
  }

  function getParentCategory(parent) {
    getApiData(BaseSetting.endpoint.parentCategory, "post", {
      type: "admin",
      parent_id: parent?.id ?? null,
    })
      .then((result) => {
        if (result?.status) {
          setParentCategoryData(result?.data || []);
        }
      })
      .catch((err) => toast(err?.message, { type: "error" }));
  }

  function getAllCategories() {
    getApiData(BaseSetting.endpoint.allCategory, "post", {
      page: 1,
      type: "admin",
      download: false,
    })
      .then((result) => {
        if (result?.status && result?.data?.allCategories) {
          setAllCategoryData(result.data.allCategories);
        }
      })
      .catch((err) => toast(err?.message, { type: "error" }));
  }

  function getCountriesForDropdown() {
    getApiData(BaseSetting.endpoint.getCountry, "get", {})
      .then((result) => {
        if (result?.status) {
          // Handle different possible response structures
          const countryData = result?.data?.countries || result?.data?.countryData || result?.data || [];
          setCountryList(Array.isArray(countryData) ? countryData : []);
        }
      })
      .catch((err) => {
        toast(err?.message || "Failed to load countries", { type: "error" });
        setCountryList([]);
      });
  }

  function getStatesByCountry(country, editRow = null) {
    if (!country) {
      setStateList([]);
      setCityList([]);
      setSelectedState(null);
      setSelectedCity(null);
      return;
    }

    // Accept country as object or string/name
    const countryValue = typeof country === "string" ? country : country?.name || country?.id;

    if (!countryValue) {
      setStateList([]);
      setCityList([]);
      return;
    }

    getApiData(BaseSetting.endpoint.getState, "post", {
      country: countryValue,
    })
      .then((result) => {
        if (result?.status) {
          // Handle different possible response structures
          const states = result?.data?.states || result?.data?.stateData || result?.data || [];
          const stateListData = Array.isArray(states)
            ? states.map((s) => ({
              id: s?.id || s?.state_id,
              name: s?.name || s?.state || s?.state_name,
              state: s?.state || s?.name || s?.state_name,
              ...s, // Keep original data
            }))
            : [];
          setStateList(stateListData);
          setCityList([]);
          setSelectedCity(null);

          // If editing, try to find and select the state
          if (editRow?.state_name && stateListData.length > 0) {
            const stateObj = stateListData.find(
              (s) => s.name === editRow.state_name ||
                s.state === editRow.state_name ||
                s.state_name === editRow.state_name
            );
            if (stateObj) {
              setSelectedState(stateObj);
              // Load cities for the selected state
              getCitiesByState(stateObj, editRow);
            }
          }
        } else {
          setStateList([]);
          setCityList([]);
        }
      })
      .catch((err) => {
        toast(err?.message || "Failed to load states", { type: "error" });
        setStateList([]);
        setCityList([]);
      });
  }

  function getCitiesByState(state, editRow = null) {
    if (!state) {
      setCityList([]);
      setSelectedCity(null);
      return;
    }

    // Accept state as object or string/name
    const stateValue = typeof state === "string" ? state : state?.name || state?.state || state?.id;

    if (!stateValue) {
      setCityList([]);
      return;
    }

    getApiData(BaseSetting.endpoint.getCity, "post", {
      state: stateValue,
    })
      .then((result) => {
        if (result?.status) {
          // Handle different possible response structures
          const cities = result?.data?.cities || result?.data?.cityData || result?.data || [];
          const cityListData = Array.isArray(cities) ? cities.map((c) => ({
            id: c?.id || c?.city_id,
            name: c?.name || c?.city || c?.city_name,
          })) : [];
          setCityList(cityListData);

          // If editing, try to find and select the city
          if (editRow?.city_name && cityListData.length > 0) {
            const cityObj = cityListData.find((c) => c.name === editRow.city_name || c.city === editRow.city_name);
            if (cityObj) {
              setSelectedCity(cityObj);
            }
          }
        } else {
          setCityList([]);
        }
      })
      .catch((err) => {
        toast(err?.message || "Failed to load cities", { type: "error" });
        setCityList([]);
      });
  }

  const onCountryChange = (country) => {
    setSelectedCountry(country);
    setSelectedState(null);
    setSelectedCity(null);
    setStateList([]);
    setCityList([]);
    if (country) {
      getStatesByCountry(country);
    }
  };

  const onStateChange = (stateObj) => {
    setSelectedState(stateObj);
    setSelectedCity(null);
    if (stateObj) {
      getCitiesByState(stateObj);
    } else {
      setCityList([]);
    }
  };

  const clearErrors = () => {
    setMetaTitleError("");
    setMetaDescriptionError("");
    setCategoryError("");
    setLocationError("");
  };

  const validate = () => {
    clearErrors();
    let hasError = false;
    if (!getSelectedCategoryId()) {
      setCategoryError("Please select a category");
      hasError = true;
    }
    if (!selectedCountry?.name) {
      setLocationError("Please select country");
      hasError = true;
    }
    if (!metaTitle?.trim()) {
      setMetaTitleError("Meta title is required");
      hasError = true;
    }
    if (!metaDescription?.trim()) {
      setMetaDescriptionError("Meta description is required");
      hasError = true;
    }
    if (_.isEmpty(tag)) {
      setMetaDescriptionError("At least one meta keyword is required");
      hasError = true;
    }
    return !hasError;
  };

  const submitCountryMeta = () => {
    if (!validate()) return;
    setDisableBtn(true);
    setCreateBtnLoading(true);

    const payload = {
      category_id: getSelectedCategoryId(),
      country: selectedCountry?.name || null,
      state: selectedState?.name || null,
      city: selectedCity?.name || null,
      title: metaTitle?.trim() || "",
      description: metaDescription?.trim() || "",
      meta_keys: tag,
      meta_description: metaDescription?.trim() || "",
      heading: metaH1?.trim() || null,
    };

    if (isEditModal) payload.id = editId;

    const endpoint = isEditModal
      ? BaseSetting.endpoint.updateCountryMeta
      : BaseSetting.endpoint.createCountryMeta;

    getApiData(endpoint, "POST", payload)
      .then((result) => {
        if (result?.status) {
          toast(result?.message, { type: "success" });
          getCountryMetaData(pageNo, pageSize);
          closeModal();
        } else {
          toast(result?.message || "Operation failed", { type: "error" });
        }
        setDisableBtn(false);
        setCreateBtnLoading(false);
      })
      .catch((err) => {
        toast(err?.message || "Error", { type: "error" });
        setDisableBtn(false);
        setCreateBtnLoading(false);
      });
  };

  const closeModal = () => {
    setOpenModal(false);
    setIsEditModal(false);
    setEditId("");
    setParentId(null);
    setSubCategoryData(null);
    setSubCategoryTwo(null);
    setSubCategoryThree(null);
    setSelectedCountry(null);
    setSelectedState(null);
    setSelectedCity(null);
    setStateList([]);
    setCityList([]);
    setMetaTitle("");
    setMetaDescription("");
    setMetaH1("");
    setMetaUrl("");
    setTag([]);
    setMetaKeywordsInput("");
    clearErrors();
  };

  const openEditModal = (row) => {
    skipApplyMetaRef.current = true;
    setEditId(row?.id);
    setIsEditModal(true);
    setOpenModal(true);
    setMetaTitle(row?.meta_title || "");
    setMetaDescription(row?.meta_description || "");
    setMetaH1(row?.meta_h1 || "");
    setMetaUrl(row?.meta_url || "");
    setTag(
      _.isArray(row?.meta_keywords)
        ? row.meta_keywords
        : _.isString(row?.meta_keyword) && row?.meta_keyword
          ? row.meta_keyword.split(",").map((s) => s.trim()).filter(Boolean)
          : []
    );

    if (row?.category_id && allCategoryData?.length) {
      const findCat = (id) => allCategoryData.find((c) => c.id === id);
      const cat = findCat(row.category_id);
      if (cat) {
        const hierarchy = [];
        let current = cat;
        while (current) {
          hierarchy.unshift(current);
          current = current.parent_id ? findCat(current.parent_id) : null;
        }
        const parent = hierarchy.find((h) => !h.parent_id);
        setParentId(parent || null);
        setSubCategoryData(hierarchy[1] || null);
        setSubCategoryTwo(hierarchy[2] || null);
        setSubCategoryThree(hierarchy[3] || null);
        if (parent) getParentCategory(parent);
      }
    } else {
      setParentId(null);
      setSubCategoryData(null);
      setSubCategoryTwo(null);
      setSubCategoryThree(null);
    }

    if (row?.country_name && countryList?.length) {
      const country = countryList.find((c) => c.name === row.country_name);
      setSelectedCountry(country || null);
      if (country) {
        getStatesByCountry(country, row);
      }
    }
  };

  const handleKeywordKeyPress = (e) => {
    if (e.key === "Enter" && metaKeywordsInput?.trim()) {
      e.preventDefault();
      setTag([...tag, metaKeywordsInput.trim()]);
      setMetaKeywordsInput("");
    }
  };

  const removeTag = (index) => {
    setTag(tag.filter((_, i) => i !== index));
  };

  const tableColumns = [
    {
      field: "id",
      headerName: "NO.",
      flex: 0.2,
      minWidth: 60,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => {
        const newStartIndex = (page - 1) * (pagination?.defaultPageSize || 25) + 1;
        const rowIndex = params.api.getRowIndexRelativeToVisibleRows(params.id);
        const idx = rowIndex >= 0 ? newStartIndex + rowIndex : params.row?.id ?? "-";
        return (
          <Typography style={{ fontFamily: FontFamily.SemiBold }}>{idx}</Typography>
        );
      },
    },
    {
      field: "category_name",
      headerName: "CATEGORY",
      flex: 1,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "country_name",
      headerName: "COUNTRY",
      flex: 0.8,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "state_name",
      headerName: "STATE",
      flex: 0.8,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "city_name",
      headerName: "CITY",
      flex: 0.8,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "meta_title",
      headerName: "META TITLE",
      flex: 1,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "meta_description",
      headerName: "META DESCRIPTION",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Typography
          sx={{
            maxWidth: 200,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {params.row?.meta_description?.replace(/<[^>]*>/g, "")?.slice(0, 60) || "—"}
        </Typography>
      ),
    },
    {
      field: "actions",
      headerName: "ACTIONS",
      flex: 0.6,
      headerAlign: "center",
      align: "center",
      filterable: false,
      renderCell: (params) => (
        <Button onClick={() => openEditModal(params.row)}>
          <Edit style={{ fontSize: 20 }} />
        </Button>
      ),
    },
  ];

  return (
    <Grid container>
      <MiniDrawer
        header="Country Meta Details"
        children={
          <>
            <Grid container justifyContent="space-between" alignItems="center" mb={2}>
              <Button
                variant="contained"
                onClick={() => {
                  closeModal();
                  getParentCategory();
                  setOpenModal(true);
                }}
              >
                Create Country Meta
              </Button>
            </Grid>

            <Grid container>
              {pageLoad ? (
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "40vh",
                  }}
                >
                  <CircularProgress style={{ color: BaseColor.primary }} size={36} />
                </Grid>
              ) : (
                <DataGrid
                  rows={countryMetaList.map((row, idx) => ({ ...row, id: row?.id ?? idx }))}
                  columns={tableColumns}
                  disableColumnMenu
                  loading={dataLoader}
                  disableDensitySelector
                  sortingMode="client"
                  paginationMode="server"
                  rowCount={totalRowCount}
                  hideFooterPagination
                  showCellVerticalBorder
                  showColumnVerticalBorder
                  disableSelectionOnClick
                  autoHeight
                  getRowHeight={() => "auto"}
                  slots={{ toolbar: GridToolbar }}
                  disableColumnFilter
                  disableColumnSelector
                  initialState={{
                    filter: {
                      filterModel: {
                        items: [],
                        quickFilterValues: [searchVal],
                      },
                    },
                  }}
                  slotProps={{
                    toolbar: {
                      printOptions: { disableToolbarButton: true },
                      csvOptions: { disableToolbarButton: true },
                      showQuickFilter: true,
                      onChange: (e) => {
                        setDataLoader(true);
                        setSearchVal(e.target.value?.toLowerCase() || "");
                        if (searchRef.current) clearTimeout(searchRef.current);
                        searchRef.current = setTimeout(() => setDataLoader(false), 300);
                      },
                    },
                  }}
                  sx={{
                    "& .MuiInputBase-root button svg.MuiSvgIcon-fontSizeSmall": { display: "none" },
                    "& .css-dwjt6a-MuiButtonBase-root-MuiIconButton-root": { display: "none" },
                  }}
                />
              )}
            </Grid>
            {!_.isEmpty(countryMetaList) && _.isArray(countryMetaList) && (
              <div
                style={{
                  marginTop: "20px",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <Pagination
                  count={pagination?.totalPage || 1}
                  page={page}
                  siblingCount={2}
                  onChange={(e, value) => {
                    setHeaderParams(value);
                    getCountryMetaData(value, pagination?.defaultPageSize || 25);
                  }}
                  sx={{ display: pageLoad ? "none" : "block" }}
                />
              </div>
            )}

            <CModal
              visible={openModal}
              title={isEditModal ? "Update Country Meta" : "Create Country Meta"}
              onClose={closeModal}
              style={{ width: "56%" }}
              children={
                <div style={{ padding: "20px" }}>
                  <Grid container rowSpacing={2}>
                    {/* Category section */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5, fontFamily: FontFamily.SemiBold }}>
                        Category
                      </Typography>
                      <CSelect
                        placeholder="Parent category"
                        fullObj
                        data={parentCategoryData}
                        value={parentId}
                        onSelect={(val) => {
                          setParentId(val);
                          setSubCategoryData(null);
                          setSubCategoryTwo(null);
                          setSubCategoryThree(null);
                          if (val) getParentCategory(val);
                        }}
                        errorMsg={categoryError}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Select
                        value={subCategoryData?.id ? subCategoryData : ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          setSubCategoryData(val || null);
                          setSubCategoryTwo(null);
                          setSubCategoryThree(null);
                        }}
                        fullWidth
                        displayEmpty
                        disabled={!parentId}
                      >
                        <MenuItem value="" disabled>
                          <em>Select subCategory</em>
                        </MenuItem>
                        {getFilteredCategories(parentId?.id)?.map((cat) => (
                          <MenuItem key={cat?.id} value={cat}>
                            {cat.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid>
                    <Grid item xs={12}>
                      <Select
                        value={subCategoryTwo?.id ? subCategoryTwo : ""}
                        onChange={(e) => {
                          setSubCategoryTwo(e.target.value || null);
                          setSubCategoryThree(null);
                        }}
                        fullWidth
                        displayEmpty
                        disabled={!subCategoryData}
                      >
                        <MenuItem value="" disabled>
                          <em>Select subCategoryTwo</em>
                        </MenuItem>
                        {getFilteredCategories(subCategoryData?.id)?.map((cat) => (
                          <MenuItem key={cat?.id} value={cat}>
                            {cat.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid>
                    <Grid item xs={12}>
                      <Select
                        value={subCategoryThree?.id ? subCategoryThree : ""}
                        onChange={(e) => setSubCategoryThree(e.target.value || null)}
                        fullWidth
                        displayEmpty
                        disabled={!subCategoryTwo}
                      >
                        <MenuItem value="" disabled>
                          <em>Select subCategoryThree</em>
                        </MenuItem>
                        {getFilteredCategories(subCategoryTwo?.id)?.map((cat) => (
                          <MenuItem key={cat?.id} value={cat}>
                            {cat.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid>

                    {/* Location: Country, State, City */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5, fontFamily: FontFamily.SemiBold }}>
                        Location
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <CSelect
                        placeholder="Country"
                        fullObj
                        data={countryList}
                        value={selectedCountry}
                        onSelect={onCountryChange}
                        errorMsg={locationError && !selectedCountry ? locationError : ""}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Select
                        value={selectedState?.name || selectedState?.state || ""}
                        onChange={(e) => {
                          const stateValue = e.target.value;
                          const stateObj = stateList.find((s) => (s.name === stateValue || s.state === stateValue));
                          onStateChange(stateObj || null);
                        }}
                        fullWidth
                        displayEmpty
                        disabled={!selectedCountry}
                      >
                        <MenuItem value="" disabled>
                          <em>Select State</em>
                        </MenuItem>
                        {stateList.map((s, idx) => {
                          const stateName = s?.name || s?.state || s?.state_name || `State ${idx}`;
                          const stateValue = s?.name || s?.state || stateName;
                          return (
                            <MenuItem key={s?.id || s?.state_id || idx} value={stateValue}>
                              {stateName}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Select
                        value={selectedCity?.name ?? ""}
                        onChange={(e) => {
                          const cityName = e.target.value;
                          const city = cityList.find((c) => c.name === cityName);
                          setSelectedCity(city || null);
                        }}
                        fullWidth
                        displayEmpty
                        disabled={!selectedState}
                      >
                        <MenuItem value="" disabled>
                          <em>Select City</em>
                        </MenuItem>
                        {cityList.map((c) => (
                          <MenuItem key={c?.id} value={c?.name}>
                            {c?.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid>

                    {/* Meta fields */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5, fontFamily: FontFamily.SemiBold }}>
                        Meta details
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <CInput
                        placeholder="Meta title"
                        value={metaTitle}
                        onChange={(val) => { setMetaTitle(val); clearErrors(); }}
                        sx={{ width: "100%" }}
                        errorMsg={metaTitleError}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <CInput
                        placeholder="Meta H1"
                        value={metaH1}
                        onChange={(val) => setMetaH1(val)}
                        sx={{ width: "100%" }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <CInput
                        placeholder="Meta URL"
                        value={metaUrl}
                        onChange={(val) => setMetaUrl(val)}
                        sx={{ width: "100%" }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <CInput
                        placeholder="Meta description"
                        value={metaDescription}
                        onChange={(val) => { setMetaDescription(val); clearErrors(); }}
                        multiline
                        rows={4}
                        maxRows={6}
                        sx={{ width: "100%" }}
                        errorMsg={metaDescriptionError}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TagsInput
                        onKeyDown={handleKeywordKeyPress}
                        value={metaKeywordsInput}
                        onChange={(val) => setMetaKeywordsInput(val)}
                        tagList={tag}
                        removeTag={removeTag}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <CButton
                        variant="contained"
                        onClick={submitCountryMeta}
                        style={{ width: "100%" }}
                        disabled={disableBtn}
                        loading={createBtnLoading}
                      >
                        {isEditModal ? "Update" : "Create"}
                      </CButton>
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        variant="outlined"
                        onClick={closeModal}
                        style={{ width: "100%" }}
                        disabled={disableBtn}
                      >
                        Cancel
                      </Button>
                    </Grid>
                  </Grid>
                </div>
              }
            />
          </>
        }
      />
    </Grid>
  );
};

export default CountryMetaDetails;
